import { useState, useEffect, useRef } from 'react';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid';
import {
  Button,
  Stack,
  CircularProgress,
  Box,
  Paper,
  LinearProgress,
  Alert,
  AlertTitle,
  Snackbar,
  GlobalStyles,
  Backdrop,
  Fab,
  Typography,
  Tooltip,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  Drawer,
  Chip,
  Avatar,
  Divider,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  List,
  ListItem
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  ArrowUpward,
  ArrowDownward,
  Search,
  Clear,
  CloudDownload,
  Close as CloseIcon,
  ChevronRight,
  Apple,
  Android,
  DeleteSweep,
  Inventory2Outlined,
  SortRounded,
  ArrowDropDown,
  ArchiveOutlined,
  PhotoCameraOutlined,
  PlaylistAddCheck,
  WarningAmberRounded,
  NotificationsActive,
  NotificationsNone,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
  ShopRounded
} from '@mui/icons-material';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

import { formatDateOnly, formatTimeOnly } from '../utils/dateUtils';
import { logAudit } from '../utils/audit';
import { dataGridSx, dataGridContainerSx } from '../utils/tableStyles';
import { isAabDownloadable, aabMsRemaining } from '../utils/aab';
import { AppDetailsDrawer, AppDetails } from './AppDetailsDrawer';
import { playStoreUrlFor, resolveIosStoreUrls, ResolvedStoreUrl } from '../utils/storeUrls';

const useStyles = makeStyles({
  refreshButton: {
    cursor: 'pointer',
    height: 40,
    color: '#181818',
    borderColor: '#181818',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: '#f6f6f6',
      backgroundColor: '#f0f0f0',
    },
  },
});

interface DataTableProps {
  url1: string;
  isMobile: boolean;
  isActive: boolean;
}

interface Row {
  id: number;
  composerAppId: string;
  appName: string;
  version: string;
  platform: string;
  composerHost: string;
  sitekey?: string | null;
  pipelineId?: string;
  domainId?: string;
  app_icon_url?: string;
  status?: string;
  user?: string;
  end_time?: string | Date;
  phase?: string;
  aabUrl?: string;
  // Server-stamped — populated by /apps from Redis. Replaces the per-browser
  // localStorage state that previously gated AAB visibility per-user.
  aabBuildInProgress?: boolean;
  aabCompletedAt?: number | null;
}


export const TableComponent = ({ url1, isMobile, isActive }: DataTableProps) => {
  const classes = useStyles();
  const { dashboardData } = useAuth();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Deep-link from other pages: ?search=<term> jumps to the table filtered to
  // that term; ?host=<composerHost> additionally opens the app-details drawer
  // (handled by a dedicated effect below so it works on live SPA navigation,
  // not just a fresh mount). The param is stripped once applied.
  const initialSearchFromUrl = urlSearchParams.get('search') || '';

  // Pagination State
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [rowCount, setRowCount] = useState(0);

  const [rows, setRows] = useState<Row[]>([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedAabRows, setSelectedAabRows] = useState<Set<number>>(new Set());
  const [selectedScreenshotRows, setSelectedScreenshotRows] = useState<Set<number>>(new Set());
  const [selectionDrawerOpen, setSelectionDrawerOpen] = useState(false);

  // Per-app detail drawer (opened by clicking a row — distinct from the
  // checkbox, which selects the row for deployment).
  const [detailsApp, setDetailsApp] = useState<AppDetails | null>(null);
  // Host passed via ?host= that we should auto-open once its row loads.
  const pendingHostRef = useRef('');

  // Resolved iOS App Store URLs keyed by bundle id. Android links are built
  // synchronously; iOS needs the backend iTunes lookup, so the Store column
  // shows a spinner until this map is populated for the visible rows.
  const [iosStoreUrls, setIosStoreUrls] = useState<Record<string, ResolvedStoreUrl | null>>({});

  // Pre-deploy confirmation modal
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; isPatch: boolean }>({ open: false, isPatch: false });

  // "Select all apps" — pulls every matching ID from the backend in one shot
  // (respects current search/platform/sort filters). Spinner state so the
  // button shows progress on large fleets.
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  // Browser notifications toggle — opt-in, persists across sessions
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  // First-time-enable info dialog so users see an example notification + the
  // "where + when does this fire" matrix before relying on it.
  const [notifHelpOpen, setNotifHelpOpen] = useState(false);

  // Per-app build status enrichment (current phase + last 5 outcomes).
  // Keyed by application id. Populated by a background poll.
  type AppBuildStatus = {
    currentPhase: string | null;
    pipelineStatus: string | null;
    last5: Array<{ status: string; createdAt: string }>;
    // Latest-build metadata for the App Details "Latest build snapshot".
    // latestPhase resolves even for terminal builds (where currentPhase is null).
    latestPhase?: string | null;
    pipelineId?: string | null;
    submitter?: string | null;
    updatedAt?: string | null;
  };
  const [appBuildStatus, setAppBuildStatus] = useState<Record<number, AppBuildStatus>>({});
  // Was a Set tracked in localStorage (per-browser). The "In Progress" indicator
  // is now derived from row.aabBuildInProgress, which the backend populates from
  // Redis so it's visible to every user, not just the one who triggered.
  const hasActiveAabRef = useRef<boolean>(false);

  const [progress, setProgress] = useState(100);
  const [triggering, setTriggering] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  const [showScrollUp, setShowScrollUp] = useState(false);
  const [cacheAge, setCacheAge] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  type SearchMode = 'q' | 'version' | 'sitekey';
  type SortBy = '' | 'composerHost' | 'version' | 'updatedAt' | 'sitekey';
  type SortOrder = 'asc' | 'desc';
  const [searchMode, setSearchMode] = useState<SearchMode>('q');
  const [searchQuery, setSearchQuery] = useState(initialSearchFromUrl);
  const [searchInput, setSearchInput] = useState(initialSearchFromUrl);
  const [platformFilter, setPlatformFilter] = useState<'' | 'ios' | 'android'>('');
  const [versionFilter, setVersionFilter] = useState('');
  // Distinct fleet-wide versions for the version filter dropdown. Sourced from
  // a dedicated backend endpoint (whole fleet, not just the current page).
  const [versionOptions, setVersionOptions] = useState<string[]>([]);
  const [siteKeyFilter, setSiteKeyFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchActiveRef = useRef(initialSearchFromUrl.length > 0);

  // After applying the deep-linked ?search= param on mount, clear it so the
  // URL doesn't keep the stale filter forever.
  useEffect(() => {
    if (initialSearchFromUrl) {
      setUrlSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('search');
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open the app-details drawer when arriving via ?host= — from App
  // Distribution / Build Cost / the command palette. Runs on every URL change
  // (the page stays mounted across SPA navigation), seeds the search so the
  // target row loads, remembers the host to open, then strips the param.
  useEffect(() => {
    const host = urlSearchParams.get('host');
    if (!isActive || !host) return;
    pendingHostRef.current = host.toLowerCase();
    setSearchMode('q');
    setSearchInput(host);
    setSearchQuery(host);
    searchActiveRef.current = true;
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setUrlSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('host');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchParams, isActive]);

  // Map a grid row to the normalised shape the detail drawer consumes.
  const toAppDetails = (row: any): AppDetails => {
    // The plain /apps row often has status "N/A"; the live per-app build poll
    // (appBuildStatus) carries the real latest status + current phase, so fold
    // it in here so the drawer's "Latest build" isn't empty.
    const enrich = appBuildStatus[row.id];
    return {
      appName: row.appName,
      composerHost: row.composerHost,
      composerAppId: row.composerAppId,
      platform: (row.platform || '').toLowerCase(),
      version: row.version,
      sitekey: row.sitekey ?? null,
      bundleIdentifier: row.bundleIdentifier ?? null,
      status: enrich?.pipelineStatus || row.status,
      // Prefer the latest build's own metadata (from the app-status enrichment)
      // over the plain /apps row, which doesn't carry build-level fields — that
      // mismatch left Phase / Pipeline ID / Submitter blank in the snapshot.
      user: enrich?.submitter || row.user,
      updatedAt: enrich?.updatedAt || row.end_time,
      appIconUrl: row.app_icon_url,
      pipelineId: enrich?.pipelineId || row.pipelineId,
      aabUrl: row.aabUrl,
      aabCompletedAt: row.aabCompletedAt ?? null,
      raw: { ...(row._raw ?? row), phase: enrich?.latestPhase ?? enrich?.currentPhase ?? (row._raw ?? row)?.phase },
    };
  };

  // Once the ?host= search above has loaded its rows, open the matching app's
  // drawer — then forget the host so filtering doesn't keep re-opening it.
  useEffect(() => {
    if (!isActive || !pendingHostRef.current || rows.length === 0) return;
    const target = pendingHostRef.current;
    const match = rows.find(r => (r.composerHost || '').toLowerCase() === target)
      || rows.find(r => (r.composerHost || '').toLowerCase().includes(target));
    if (match) {
      pendingHostRef.current = '';
      setDetailsApp(toAppDetails(match));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isActive]);

  // Resolve the App Store URL for every iOS bundle id on the current page in
  // one pass (backend lookup, Redis-cached). Android needs no lookup.
  useEffect(() => {
    const iosBundleIds = Array.from(new Set(
      rows
        .filter(r => (r.platform || '').toLowerCase() === 'ios')
        .map(r => (r as any).bundleIdentifier as string | undefined)
        .filter(Boolean) as string[],
    ));
    if (iosBundleIds.length === 0) return;
    let cancelled = false;
    resolveIosStoreUrls(iosBundleIds).then(resolved => {
      if (!cancelled) setIosStoreUrls(prev => ({ ...prev, ...resolved }));
    });
    return () => { cancelled = true; };
  }, [rows]);

  // Store link state for a row: resolved (open-able), pending (iOS lookup in
  // flight), or unavailable (no bundle id / not publicly listed).
  type StoreLinkState =
    | { kind: 'resolved'; target: ResolvedStoreUrl }
    | { kind: 'pending' }
    | { kind: 'unavailable' };
  const storeLinkFor = (row: any): StoreLinkState => {
    const platform = (row.platform || '').toLowerCase();
    const bundleId = (row.bundleIdentifier as string) || '';
    if (!bundleId) return { kind: 'unavailable' };
    if (platform === 'android') {
      const target = playStoreUrlFor(bundleId);
      return target ? { kind: 'resolved', target } : { kind: 'unavailable' };
    }
    if (platform === 'ios') {
      if (bundleId in iosStoreUrls) {
        const target = iosStoreUrls[bundleId];
        return target ? { kind: 'resolved', target } : { kind: 'unavailable' };
      }
      return { kind: 'pending' };
    }
    return { kind: 'unavailable' };
  };
  const paginationModelRef = useRef(paginationModel);
  const searchQueryRef = useRef(searchQuery);
  const platformFilterRef = useRef(platformFilter);
  const versionFilterRef = useRef(versionFilter);
  const siteKeyFilterRef = useRef(siteKeyFilter);
  const sortByRef = useRef(sortBy);
  const sortOrderRef = useRef(sortOrder);

  const rowCacheRef = useRef<Map<number, Row>>(new Map());

  const topRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const token = localStorage.getItem('token');

  const openSnack = (msg: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackMsg(msg);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const deselectRow = (id: number) => {
    setSelectedRows(prev => prev.filter(rowId => rowId !== id));
    setSelectedAabRows(prev => { const s = new Set(prev); s.delete(id); return s; });
    setSelectedScreenshotRows(prev => { const s = new Set(prev); s.delete(id); return s; });
  };

  const clearAllSelections = () => {
    setSelectedRows([]);
    setSelectedAabRows(new Set());
    setSelectedScreenshotRows(new Set());
  };

  // -------------------- Select All apps (cross-page) --------------------
  const selectAllApps = async () => {
    setIsSelectingAll(true);
    try {
      const params = new URLSearchParams();
      params.append('ids_only', 'true');
      const currentSearch = searchQueryRef.current;
      const currentPlatform = platformFilterRef.current;
      const currentVersion = versionFilterRef.current;
      const currentSiteKey = siteKeyFilterRef.current;
      const currentSortBy = sortByRef.current;
      const currentSortOrder = sortOrderRef.current;
      if (currentSearch) params.append('search', currentSearch);
      if (currentPlatform) params.append('platform', currentPlatform);
      if (currentVersion) params.append('version', currentVersion);
      if (currentSiteKey) params.append('sitekey', currentSiteKey);
      if (currentSortBy) {
        params.append('sortBy', currentSortBy);
        params.append('sortOrder', currentSortOrder);
      }
      const tok = localStorage.getItem('token');
      const resp = await axios.get(`/api/deployment/apps?${params.toString()}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const ids: number[] = resp.data?.ids || [];
      if (ids.length === 0) {
        openSnack('No apps match the current filters.', 'info');
        return;
      }
      setSelectedRows(prev => Array.from(new Set([...prev, ...ids])));
      openSnack(`Selected all ${ids.length} matching apps.`, 'success');
    } catch (err: any) {
      openSnack(err?.response?.data?.error || 'Failed to fetch matching apps.', 'error');
    } finally {
      setIsSelectingAll(false);
    }
  };

  // Unselect everything currently selected (across all pages). Mirrors the
  // drawer's "Clear all selections" but also surfaces a confirmation snack.
  const unselectAll = () => {
    const count = selectedRows.length;
    if (count === 0) return;
    clearAllSelections();
    openSnack(`Unselected ${count} app${count === 1 ? '' : 's'}.`, 'success');
  };

  // -------------------- Browser notifications --------------------
  const NOTIFS_KEY = 'deployNotifsEnabled:v1';
  const PENDING_TRIGGERS_KEY = 'deployPendingTriggers:v1';

  useEffect(() => {
    try {
      setNotificationsEnabled(localStorage.getItem(NOTIFS_KEY) === 'true');
    } catch { /* ignore */ }
  }, []);

  const toggleNotifications = async () => {
    if (notificationsEnabled) {
      setNotificationsEnabled(false);
      try { localStorage.setItem(NOTIFS_KEY, 'false'); } catch { /* ignore */ }
      openSnack('Build notifications disabled.', 'info');
      return;
    }
    if (!('Notification' in window)) {
      openSnack('This browser does not support notifications.', 'warning');
      return;
    }
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission === 'granted') {
      setNotificationsEnabled(true);
      try { localStorage.setItem(NOTIFS_KEY, 'true'); } catch { /* ignore */ }
      openSnack('Build notifications enabled.', 'success');
      // Surface the "how this works" dialog. The confirmation sample
      // notification fires only when the user clicks "Got it" — that way the
      // alert reaches them after they've acknowledged the prompt, instead of
      // popping behind the modal where it's easy to miss.
      setNotifHelpOpen(true);
    } else {
      openSnack('Notification permission denied. Enable it from your browser site settings.', 'warning');
    }
  };

  // Tracks pipelines this browser kicked off so we can fire a single
  // browser notification when each one reaches a terminal state.
  type PendingTrigger = { pipelineId: string; appName: string; platform: string; version: string; appId: number };
  const loadPendingTriggers = (): PendingTrigger[] => {
    try {
      const raw = localStorage.getItem(PENDING_TRIGGERS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };
  const savePendingTriggers = (next: PendingTrigger[]) => {
    try { localStorage.setItem(PENDING_TRIGGERS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  // -------------------- Build status poller --------------------
  // One poll per ~12s while the page is active. Pulls phase + last-5 outcomes
  // for the currently rendered rows, populates appBuildStatus, and fires a
  // browser notification for any pending trigger that just flipped terminal.
  const TERMINAL_RE = /^(success|failed|cancelled|canceled)$/i;

  const pollBuildStatus = async () => {
    if (rows.length === 0) return;
    const ids = rows.map(r => r.id).join(',');
    try {
      const tok = localStorage.getItem('token');
      const resp = await axios.get(`/api/builds/app-status?ids=${encodeURIComponent(ids)}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const items: Record<number, AppBuildStatus> = resp.data?.items || {};
      setAppBuildStatus(prev => ({ ...prev, ...items }));

      // Browser notifications — fire once per pending trigger when its app's
      // latest build reaches a terminal state, then clear it from pending.
      if (notificationsEnabled && 'Notification' in window && Notification.permission === 'granted') {
        const pending = loadPendingTriggers();
        if (pending.length > 0) {
          const stillPending: PendingTrigger[] = [];
          for (const trigger of pending) {
            const appStatus = items[trigger.appId];
            const latestStatus = appStatus?.pipelineStatus || '';
            if (latestStatus && TERMINAL_RE.test(latestStatus)) {
              const isSuccess = /^success$/i.test(latestStatus);
              try {
                new Notification(
                  isSuccess ? '✅ Build succeeded' : '❌ Build failed',
                  {
                    body: `${trigger.appName} (${trigger.platform}) v${trigger.version} — ${latestStatus}`,
                    tag: `build-${trigger.pipelineId}`,
                  },
                );
              } catch { /* notification refused — drop silently */ }
            } else {
              stillPending.push(trigger);
            }
          }
          if (stillPending.length !== pending.length) {
            savePendingTriggers(stillPending);
          }
        }
      }
    } catch (err) {
      // Polling failures are non-fatal — just log and try again next tick.
      console.warn('[app-status poll]', err);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    // Initial fetch right after rows load
    pollBuildStatus();
    const id = setInterval(pollBuildStatus, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, rows, notificationsEnabled]);


  // Auto-close the drawer when the selection drops to zero so the user isn't
  // left staring at an empty panel after clearing everything.
  useEffect(() => {
    if (selectedRows.length === 0 && selectionDrawerOpen) {
      setSelectionDrawerOpen(false);
    }
  }, [selectedRows.length, selectionDrawerOpen]);

  const smoothScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollDown = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    const onScroll = () => setShowScrollUp(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Tick every minute so the AAB 2-hour window re-evaluates in the UI
  const [, setAabTick] = useState(0);
  useEffect(() => {
    // aabUrl alone isn't a reason to keep ticking — it can linger long after
    // the download window closes. Only re-render while a build is in progress
    // or a link is still within its (URL-derived) download window.
    const hasAab = rows.some(r =>
      r.aabBuildInProgress || isAabDownloadable(r.aabUrl, r.aabCompletedAt)
    );
    if (!hasAab) return;
    const timer = setInterval(() => setAabTick(t => t + 1), 60000);
    return () => clearInterval(timer);
  }, [rows]);

  // Track whether any row has an active AAB build, so polling can speed up
  // while builds are in progress without re-creating the interval each tick.
  useEffect(() => {
    hasActiveAabRef.current = rows.some(r => r.aabBuildInProgress);
  }, [rows]);

  // Fetch Tags
  useEffect(() => {
    if (!isActive) return;
    const fetchTags = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/appdata/gitlab-tags', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.data?.tags?.length > 0) {
          setSelectedTag(res.data.tags[0].name);
        }
      } catch (error) {
        console.error('Failed to fetch tags', error);
      }
    };
    fetchTags();
  }, [isActive]);

  // Fetch the distinct version list for the filter dropdown. Re-fetched when the
  // platform toggle changes so the options stay scoped to the visible platform.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const fetchVersions = async () => {
      try {
        const tok = localStorage.getItem('token');
        const qs = platformFilter ? `?platform=${encodeURIComponent(platformFilter)}` : '';
        const res = await axios.get(`/api/deployment/apps/versions${qs}`, {
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        if (cancelled) return;
        const list: string[] = res.data?.versions || [];
        setVersionOptions(list);
        // Drop a selected version that no longer exists for the chosen platform,
        // otherwise the Select shows an out-of-range value and the grid filters
        // to nothing.
        setVersionFilter(prev => (prev && !list.includes(prev) ? '' : prev));
      } catch (error) {
        console.error('Failed to fetch app versions', error);
      }
    };
    fetchVersions();
    return () => { cancelled = true; };
  }, [isActive, platformFilter]);

  // Pick a version from the dropdown — drives the same server-side `version`
  // filter the version search-mode uses, so it works across all pages. Clearing
  // it ('') returns to the full unfiltered list.
  const handleVersionFilterSelect = (value: string) => {
    setVersionFilter(value);
    // Keep the text search box from also constraining the version column.
    if (searchMode === 'version') {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      setSearchInput('');
    }
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  /* --------------------------------------------------------------
   * Server-Side Pagination & Fetch Logic
   * -------------------------------------------------------------- */

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    searchActiveRef.current = value.length > 0;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const trimmed = value.trim();
      if (searchMode === 'version') {
        setSearchQuery('');
        setSiteKeyFilter('');
        setVersionFilter(trimmed);
      } else if (searchMode === 'sitekey') {
        setSearchQuery('');
        setVersionFilter('');
        setSiteKeyFilter(trimmed);
      } else {
        setVersionFilter('');
        setSiteKeyFilter('');
        setSearchQuery(trimmed);
      }
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, 400);
  };

  const handleSearchModeChange = (mode: SearchMode) => {
    if (mode === searchMode) return;
    setSearchMode(mode);
    // Switching mode wipes the input — the existing text doesn't make sense
    // in the new column (e.g. "schoolbuzz" as a version, or "1.2.3" as a host).
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchInput('');
    searchActiveRef.current = false;
    setSearchQuery('');
    setVersionFilter('');
    setSiteKeyFilter('');
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleClearSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchInput('');
    searchActiveRef.current = false;
    setSearchQuery('');
    setVersionFilter('');
    setSiteKeyFilter('');
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handlePlatformChange = (value: '' | 'ios' | 'android') => {
    setPlatformFilter(value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleClearAllFilters = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchInput('');
    setSearchQuery('');
    setVersionFilter('');
    setSiteKeyFilter('');
    searchActiveRef.current = false;
    setPlatformFilter('');
    setSortBy('');
    setSortOrder('asc');
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleSortSelect = (field: SortBy, order: SortOrder) => {
    setSortBy(field);
    setSortOrder(order);
    setSortMenuAnchor(null);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const SORT_OPTIONS: Array<{ field: Exclude<SortBy, ''>; order: SortOrder; label: string }> = [
    { field: 'composerHost', order: 'asc', label: 'Composer Host (A → Z)' },
    { field: 'composerHost', order: 'desc', label: 'Composer Host (Z → A)' },
    { field: 'version', order: 'asc', label: 'Version (Low → High)' },
    { field: 'version', order: 'desc', label: 'Version (High → Low)' },
    { field: 'updatedAt', order: 'desc', label: 'Last Updated (Newest)' },
    { field: 'updatedAt', order: 'asc', label: 'Last Updated (Oldest)' },
    { field: 'sitekey', order: 'asc', label: 'Site Key (A → Z)' },
    { field: 'sitekey', order: 'desc', label: 'Site Key (Z → A)' },
  ];

  const currentSortLabel = sortBy
    ? SORT_OPTIONS.find(opt => opt.field === sortBy && opt.order === sortOrder)?.label || 'Sort'
    : 'Sort';

  // Keep refs in sync so polling always uses the latest page/search values
  useEffect(() => { paginationModelRef.current = paginationModel; }, [paginationModel]);
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);
  useEffect(() => { platformFilterRef.current = platformFilter; }, [platformFilter]);
  useEffect(() => { versionFilterRef.current = versionFilter; }, [versionFilter]);
  useEffect(() => { siteKeyFilterRef.current = siteKeyFilter; }, [siteKeyFilter]);
  useEffect(() => { sortByRef.current = sortBy; }, [sortBy]);
  useEffect(() => { sortOrderRef.current = sortOrder; }, [sortOrder]);

  // Clear the search box when the user leaves the page, so coming back from
  // another menu starts with a clean (unfiltered) table rather than the stale
  // previous query. The page stays mounted (HomeComponent toggles display), so
  // without this the search input would persist across navigations.
  const wasActiveRef = useRef(isActive);
  useEffect(() => {
    if (wasActiveRef.current && !isActive) {
      handleClearSearch();
      setDetailsApp(null); // close the app-details drawer when leaving the page
    }
    wasActiveRef.current = isActive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Load data when pagination, search, or filters change
  useEffect(() => {
    if (isActive) {
      loadTableData();
    }
  }, [paginationModel, isActive, searchQuery, platformFilter, versionFilter, siteKeyFilter, sortBy, sortOrder]);

  async function loadTableData(showSnack = false) {
    if (!isActive) return;
    try {
      setIsRefreshing(true);

      const token = localStorage.getItem('token');
      const currentPagination = paginationModelRef.current;
      const currentSearch = searchQueryRef.current;
      const currentPlatform = platformFilterRef.current;
      const currentVersion = versionFilterRef.current;
      const currentSiteKey = siteKeyFilterRef.current;
      const currentSortBy = sortByRef.current;
      const currentSortOrder = sortOrderRef.current;
      const params = new URLSearchParams();
      params.append('page', (currentPagination.page + 1).toString()); // API is 1-indexed
      params.append('limit', currentPagination.pageSize.toString());
      if (currentSearch) params.append('search', currentSearch);
      if (currentPlatform) params.append('platform', currentPlatform);
      // Worker's search endpoint requires `q` for the version filter to apply.
      // We still send it when the user supplies a version without text — the
      // backend will silently drop it in that branch, which beats showing the
      // user stale results from an old request.
      if (currentVersion) params.append('version', currentVersion);
      if (currentSiteKey) params.append('sitekey', currentSiteKey);
      if (currentSortBy) {
        params.append('sortBy', currentSortBy);
        params.append('sortOrder', currentSortOrder);
      }

      const response = await axios.get(`/api/deployment/apps?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const { items, total } = response.data;

      // Local Queue Sync Logic
      const deployedItems = JSON.parse(localStorage.getItem('deployedItems') || '{}');
      const now = Date.now();

      const processedRows = items.map((item: any) => {
        const platform = (item.platform || '').toLowerCase();
        // Construct keys to check local state
        const key = `${item.domain}:${item.id}:${platform}`;
        const local = deployedItems[key];

        let finalStatus = item.status;

        if (local) {
          const isFresh = (now - local.timestamp) < 600000; // 10 mins
          if (isFresh && item.status !== 'success' && item.status !== 'failed' && item.status !== 'cancelled') {
            finalStatus = 'Queued';
          } else if (!isFresh) {
            delete deployedItems[key];
            localStorage.setItem('deployedItems', JSON.stringify(deployedItems));
          }
        }

        return {
          ...item,
          status: finalStatus
        };
      });

      setRows(processedRows);
      setRowCount(total || 0);

      // Cache row data for cross-page selection
      processedRows.forEach((row: Row) => {
        rowCacheRef.current.set(row.id, row);
      });

      // AAB completion + window state now lives in Redis on the backend
      // (see app-deployment.route.ts /apps reconcile block) and is delivered
      // on each row as `aabBuildInProgress` and `aabCompletedAt`. No more
      // per-browser localStorage divergence.

      if (showSnack) openSnack('Table refreshed.', 'success');
      setCacheAge(0);
      setErrorMessage(null);
    } catch (error: any) {
      console.error('Fetch error:', error);
      const msg = error.response?.data?.error || error.message || 'Error fetching data';
      setErrorMessage(msg);
    } finally {
      setIsRefreshing(false);
    }
  }

  // Track idle without tearing down the poll loop. An early-return here used to
  // kill polling entirely, and nothing on this view clears isSystemIdle — so the
  // table froze until a full reload. Instead we keep the loop alive and skip only
  // the silent background refresh while idle (mirrors the ReportsComponent fix).
  const isSystemIdleRef = useRef(false);
  useEffect(() => { isSystemIdleRef.current = !!dashboardData?.isSystemIdle; }, [dashboardData?.isSystemIdle]);

  // Polling
  useEffect(() => {
    let pollTimeoutId: NodeJS.Timeout;
    let cacheAgeIntervalId: NodeJS.Timeout;

    const poll = async () => {
      const filtersActive = searchActiveRef.current
        || !!platformFilterRef.current
        || !!versionFilterRef.current
        || !!siteKeyFilterRef.current
        || !!sortByRef.current;
      if (isActive && !filtersActive && !isSystemIdleRef.current) {
        // Silent refresh — skip when any filter is active (keep results stable)
        // or while the system is idle (spare D1); the loop keeps running so it
        // resumes automatically once activity returns.
        await loadTableData(false);
      }
      // Poll faster while AAB builds are in progress so the new URL is picked
      // up within seconds of the backend updating it.
      const delay = hasActiveAabRef.current
        ? 5000 + Math.random() * 2000
        : 45000 + Math.random() * 10000;
      pollTimeoutId = setTimeout(poll, delay);
    };

    poll();

    cacheAgeIntervalId = setInterval(() => {
      if (!isActive) return;
      setCacheAge(prev => {
        const newAge = prev + 1;
        const approximateInterval = 45;
        const remaining = Math.max(0, approximateInterval - (newAge % (approximateInterval + 5)));
        setProgress((remaining / approximateInterval) * 100);
        return newAge;
      });
    }, 1000);

    return () => {
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
      if (cacheAgeIntervalId) clearInterval(cacheAgeIntervalId);
    };
  }, [isActive]);

  /* --------------------------------------------------------------
   * Helper: Increment Patch Version
   * -------------------------------------------------------------- */
  function incrementPatchVersion(version: string): string {
    if (!version || version === 'N/A') return '1.0.1';
    const parts = version.split('.').map(part => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });
    if (parts.length < 3) {
      while (parts.length < 3) parts.push(0);
    }
    parts[parts.length - 1] = parts[parts.length - 1] + 1;
    return parts.join('.');
  }

  /* --------------------------------------------------------------
   * Trigger (Deploy) logic
   * -------------------------------------------------------------- */
  function handleTriggerClick(isPatch = false) {
    if (selectedRows.length === 0) {
      openSnack('No apps selected.', 'warning');
      return;
    }
    if (selectedRows.length > 18) {
      openSnack('Maximum 18 apps can be selected per trigger to avoid GitLab minutes exhaustion.', 'error');
      return;
    }
    setConfirmDialog({ open: true, isPatch });
  }

  // Derived: which selected apps already have an in-flight pipeline?
  const inFlightSelected = (): Row[] => {
    return selectedRows
      .map(id => rowCacheRef.current.get(id))
      .filter((r): r is Row => !!r && /queue|running|pending/i.test(r.status || ''));
  };

  async function executeTrigger(isPatch: boolean) {
    setConfirmDialog({ open: false, isPatch: false });
    setTriggering(true);
    openSnack(isPatch ? 'Initiating patch...' : 'Initiating deploy...', 'info');

    const targets = selectedRows
      .map(id => rowCacheRef.current.get(id))
      .filter((r): r is Row => r !== undefined);
    const deployedItems = JSON.parse(localStorage.getItem('deployedItems') || '{}');
    const now = Date.now();

    targets.forEach((t) => {
      const key = `${t.composerHost}:${t.id}:${t.platform.toLowerCase()}`;
      deployedItems[key] = {
        pipelineId: 'pending...',
        timestamp: now,
        status: 'queued'
      };
    });
    localStorage.setItem('deployedItems', JSON.stringify(deployedItems));

    // Optimistic UI Update
    loadTableData(false);

    // Rows not on the currently rendered page come from rowCacheRef and may
    // hold a stale aabUrl. Sending a stale URL would let the backend falsely
    // detect "completion" the moment its snapshot disagreed with the cached
    // value. Only forward aabUrl for rows we know are fresh.
    const currentPageIds = new Set(rows.map(r => r.id));

    const payloadData = targets.map(r => {
      const platform = r.platform.toLowerCase();
      const payload: any = {
        id: r.id,
        composerAppId: r.composerAppId,
        composerHost: r.composerHost,
        platform: platform,
        app_name: r.appName,
        version: isPatch ? incrementPatchVersion(r.version) : selectedTag,
        domain: r.composerHost,
        // Only forward aabUrl when the row was rendered on the current page;
        // off-page rows come from rowCacheRef and may be stale. Omitting the
        // field lets the backend treat the snapshot as unknown.
        ...(currentPageIds.has(r.id) ? { aabUrl: r.aabUrl ?? null } : {}),
      };

      // Add generateAab flag for Android builds
      if (platform === 'android' && selectedAabRows.has(r.id)) {
        payload.generateAab = true;
      }

      // Add uploadScreenshot flag for iOS builds
      if (platform === 'ios' && selectedScreenshotRows.has(r.id)) {
        payload.uploadScreenshot = true;
      }

      return payload;
    });

    try {
      const response = await axios.post(url1, {
        selectedRows: payloadData,
        incrementPatch: isPatch
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }
      });

      if (response.data && response.data.pipelineList) {
        const updatedDeployedItems = JSON.parse(localStorage.getItem('deployedItems') || '{}');
        const newPendingTriggers: PendingTrigger[] = [];
        response.data.pipelineList.forEach((pl: any) => {
          const row = targets.find(t => `${t.composerHost}:${t.composerAppId}` === pl.domainId);
          if (row) {
            const key = `${row.composerHost}:${row.id}:${row.platform.toLowerCase()}`;
            updatedDeployedItems[key] = {
              pipelineId: pl.pipelineId,
              timestamp: now,
              status: 'queued'
            };
            if (pl.pipelineId) {
              newPendingTriggers.push({
                pipelineId: String(pl.pipelineId),
                appName: row.appName,
                platform: row.platform,
                version: isPatch ? incrementPatchVersion(row.version) : selectedTag,
                appId: row.id,
              });
            }
          }
        });
        localStorage.setItem('deployedItems', JSON.stringify(updatedDeployedItems));
        if (newPendingTriggers.length > 0) {
          savePendingTriggers([...loadPendingTriggers(), ...newPendingTriggers]);
        }

        // AAB in-progress / completion / baseline state is now tracked in
        // Redis on the backend (markAabBuildStarted is called from /trigger).
        // The next /apps poll will surface aabBuildInProgress=true to every
        // user, not just this browser.

        loadTableData(true);
        openSnack('Deployment triggered successfully!', 'success');

        // Audit log: one entry per pipeline kicked off. The backend stamps
        // the actor + IP server-side from the OIDC session. Fire-and-forget.
        for (const pl of (response.data.pipelineList || [])) {
          const row = targets.find(t => `${t.composerHost}:${t.composerAppId}` === pl.domainId);
          logAudit({
            action: isPatch ? 'deploy' : 'deploy_batch',
            targetType: 'pipeline',
            targetId: String(pl.pipelineId || 'pending'),
            metadata: {
              isPatch,
              composerHost: row?.composerHost,
              composerAppId: row?.composerAppId,
              platform: row?.platform,
              appName: row?.appName,
              version: isPatch ? incrementPatchVersion(row?.version || '') : selectedTag,
              generateAab: row && row.platform.toLowerCase() === 'android' ? selectedAabRows.has(row.id) : undefined,
              uploadScreenshot: row && row.platform.toLowerCase() === 'ios' ? selectedScreenshotRows.has(row.id) : undefined,
              batchSize: response.data.pipelineList.length,
            },
          });
        }
      }

    } catch (error: any) {
      console.error('Trigger error:', error);
      const msg = error.response?.data?.message || error.message || 'Trigger failed';
      openSnack(msg, 'error');
      // Cleanup
      const cleanupItems = JSON.parse(localStorage.getItem('deployedItems') || '{}');
      targets.forEach(t => {
        const key = `${t.composerHost}:${t.id}:${t.platform.toLowerCase()}`;
        delete cleanupItems[key];
      });
      localStorage.setItem('deployedItems', JSON.stringify(cleanupItems));
      // Backend Redis state will be cleared when the next /apps poll observes
      // a failed status on the row, so no client cleanup is needed.
      loadTableData(false);
    } finally {
      setTriggering(false);
      setSelectedRows([]);
      setSelectedAabRows(new Set());
      setSelectedScreenshotRows(new Set());
    }
  }

  /* --------------------------------------------------------------
   * Columns
   * -------------------------------------------------------------- */
  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 64, disableColumnMenu: true, sortable: false },
    {
      field: 'sitekey',
      headerName: 'Site Key',
      flex: 1.5,
      minWidth: 190,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const v = (params.value as string) || '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" noWrap sx={{ color: v ? '#0f172a' : 'text.disabled' }}>
              {v || '—'}
            </Typography>
          </Box>
        );
      },
    },
    { field: 'composerAppId', headerName: 'App ID', width: 80, disableColumnMenu: true, sortable: false },
    {
      field: 'app_icon_url',
      headerName: 'Icon',
      width: 58,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <img
            src={params.value as string}
            alt="icon"
            style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', display: 'block' }}
            onError={(e: any) => { e.target.src = '/default-app-icon.svg'; }}
          />
        </Box>
      ),
    },
    { field: 'appName', headerName: 'App Name', flex: 1.6, minWidth: 180, disableColumnMenu: true, sortable: false },
    { field: 'version', headerName: 'Version', width: 84, disableColumnMenu: true, sortable: false },
    { field: 'platform', headerName: 'Platform', width: 90, disableColumnMenu: true, sortable: false },
    {
      // Direct link to the public store listing. Android → Play Store (built
      // from the bundle id); iOS → App Store (resolved via the backend lookup).
      field: 'storeLink',
      headerName: 'Store',
      width: 72,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as Row;
        const isIos = (row.platform || '').toLowerCase() === 'ios';
        const link = storeLinkFor(row);
        if (link.kind === 'pending') {
          return (
            <Tooltip title="Looking up App Store listing…" arrow>
              <span><IconButton size="small" disabled sx={{ width: 32, height: 32 }}><CircularProgress size={14} sx={{ color: 'text.disabled' }} /></IconButton></span>
            </Tooltip>
          );
        }
        if (link.kind === 'unavailable') {
          const reason = !(row as any).bundleIdentifier
            ? 'No bundle identifier on file — store link unavailable.'
            : isIos
              ? 'Not listed on the public App Store.'
              : 'No bundle identifier on file — store link unavailable.';
          return (
            <Tooltip title={reason} arrow>
              <span><IconButton size="small" disabled sx={{ width: 32, height: 32 }}><ShopRounded sx={{ fontSize: 16 }} /></IconButton></span>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={`Open in ${link.target.label}`} arrow>
            <IconButton
              size="small"
              href={link.target.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${row.appName} in ${link.target.label}`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: 32,
                height: 32,
                color: isIos ? '#0f172a' : '#1b8a47',
                bgcolor: isIos ? 'rgba(15, 23, 42, 0.08)' : 'rgba(61, 220, 132, 0.16)',
                '&:hover': { bgcolor: isIos ? 'rgba(15, 23, 42, 0.16)' : 'rgba(61, 220, 132, 0.28)' },
              }}
            >
              {isIos ? <Apple sx={{ fontSize: 16 }} /> : <Android sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'buildStatus',
      headerName: 'Build Status',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as Row;
        const enrich = appBuildStatus[row.id];
        const pipelineStatus = (enrich?.pipelineStatus || row.status || '').toLowerCase();
        const phase = enrich?.currentPhase;
        const last5 = enrich?.last5 || [];

        // 6 canonical phases, used to compute a coarse progress fraction.
        const PHASE_SEQUENCE = ['npm_build', 'update_configs', 'fastlane', 'code_signing', 'upload', 'finalize'];
        const phaseIdx = phase ? PHASE_SEQUENCE.indexOf(phase) : -1;
        const phaseTotal = PHASE_SEQUENCE.length;
        const progress = phaseIdx >= 0 ? ((phaseIdx + 1) / phaseTotal) * 100 : null;

        const isRunning = /queue|running|pending/i.test(pipelineStatus);
        const isFailed = /fail|cancel/i.test(pipelineStatus);
        const isSuccess = /success/i.test(pipelineStatus);

        const dotColor = (s: string) => {
          const ss = (s || '').toLowerCase();
          if (/success/.test(ss)) return '#16a34a';
          if (/fail/.test(ss)) return '#dc2626';
          if (/cancel/.test(ss)) return '#f59e0b';
          if (/queue|running|pending/.test(ss)) return '#3b82f6';
          return '#cbd5e1';
        };

        return (
          <Stack spacing={0.5} sx={{ width: '100%', height: '100%', py: 0.5, alignItems: 'center', justifyContent: 'center' }}>
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
              {isRunning && (
                <Chip
                  size="small"
                  label={phase ? `${phase.replace(/_/g, ' ')}${phaseIdx >= 0 ? ` ${phaseIdx + 1}/${phaseTotal}` : ''}` : 'Running'}
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(59, 130, 246, 0.12)', color: '#1d4ed8', textTransform: 'capitalize' }}
                />
              )}
              {isSuccess && (
                <Chip size="small" icon={<CheckCircle sx={{ fontSize: 13 }} />} label="Success" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(22, 163, 74, 0.12)', color: '#15803d', '& .MuiChip-icon': { color: '#15803d', ml: 0.5 } }} />
              )}
              {isFailed && (
                <Chip size="small" icon={<ErrorOutline sx={{ fontSize: 13 }} />} label={/cancel/i.test(pipelineStatus) ? 'Cancelled' : 'Failed'} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(220, 38, 38, 0.12)', color: '#b91c1c', '& .MuiChip-icon': { color: '#b91c1c', ml: 0.5 } }} />
              )}
              {!isRunning && !isSuccess && !isFailed && (
                <Chip size="small" icon={<HourglassEmpty sx={{ fontSize: 13 }} />} label="No builds" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b', '& .MuiChip-icon': { color: '#64748b', ml: 0.5 } }} />
              )}
            </Stack>
            {isRunning && progress !== null && (
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  width: '100%',
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(59, 130, 246, 0.12)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' },
                }}
              />
            )}
            {last5.length > 0 && (
              <Tooltip
                arrow
                placement="top"
                title={
                  <Box sx={{ fontSize: '0.7rem' }}>
                    {last5.map((b, i) => (
                      <div key={i}>
                        #{last5.length - i}: {b.status} — {new Date(b.createdAt).toLocaleString()}
                      </div>
                    ))}
                  </Box>
                }
              >
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                  {last5.map((b, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: dotColor(b.status),
                        border: '1px solid rgba(0,0,0,0.05)',
                      }}
                    />
                  ))}
                  {last5.length < 5 && Array.from({ length: 5 - last5.length }).map((_, i) => (
                    <Box
                      key={`pad-${i}`}
                      sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#e2e8f0' }}
                    />
                  ))}
                </Stack>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'generateAab',
      headerName: 'AAB',
      width: 72,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isIOS = params.row.platform?.toLowerCase() === 'ios';
        const isChecked = selectedAabRows.has(params.row.id);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Checkbox
              checked={isChecked}
              disabled={isIOS}
              onClick={(e) => e.stopPropagation()}
              onChange={() => {
                const newSet = new Set(selectedAabRows);
                if (isChecked) {
                  newSet.delete(params.row.id);
                } else {
                  newSet.add(params.row.id);
                  setSelectedRows(prev => prev.includes(params.row.id) ? prev : [...prev, params.row.id]);
                }
                setSelectedAabRows(newSet);
              }}
              sx={{
                color: isIOS ? 'action.disabled' : 'primary.main',
                '&.Mui-checked': {
                  color: 'primary.main',
                },
                '&.Mui-disabled': {
                  color: 'action.disabled',
                }
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'uploadScreenshot',
      headerName: 'Screenshot',
      width: 96,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isAndroid = params.row.platform?.toLowerCase() === 'android';
        const isChecked = selectedScreenshotRows.has(params.row.id);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Checkbox
              checked={isChecked}
              disabled={isAndroid}
              onClick={(e) => e.stopPropagation()}
              onChange={() => {
                const newSet = new Set(selectedScreenshotRows);
                if (isChecked) {
                  newSet.delete(params.row.id);
                } else {
                  newSet.add(params.row.id);
                  setSelectedRows(prev => prev.includes(params.row.id) ? prev : [...prev, params.row.id]);
                }
                setSelectedScreenshotRows(newSet);
              }}
              sx={{
                color: isAndroid ? 'action.disabled' : 'primary.main',
                '&.Mui-checked': {
                  color: 'primary.main',
                },
                '&.Mui-disabled': {
                  color: 'action.disabled',
                }
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'aabDownload',
      headerName: 'AAB Download',
      width: 116,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isAndroid = params.row.platform?.toLowerCase() === 'android';
        if (!isAndroid) {
          return <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>;
        }

        const aabUrl = params.row.aabUrl;

        // The presigned URL carries its own expiry — trust that. A missing
        // Redis completion stamp used to mark a freshly-built link "expired"
        // (new-app / CI-pushed builds never get that stamp). We fall back to
        // the completion-timestamp window only when the URL has no expiry.
        const aabCompletedAt: number = params.row.aabCompletedAt || 0;
        const isWithinWindow = isAabDownloadable(aabUrl, aabCompletedAt);

        // Show "In Progress" first — active AAB build takes priority over stale/expired URL
        if (params.row.aabBuildInProgress) {
          return (
            <Tooltip title="AAB is being generated, check back soon">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ opacity: 0.6 }}>
                  <CircularProgress size={12} />
                  <Typography variant="caption" sx={{ fontStyle: 'italic' }}>In Progress</Typography>
                </Stack>
              </Box>
            </Tooltip>
          );
        }

        if (!aabUrl) {
          return <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>;
        }

        if (isWithinWindow) {
          const minsLeft = Math.ceil(aabMsRemaining(aabUrl, aabCompletedAt) / 60000);
          return (
            <Tooltip title={`Download AAB file (available for ${minsLeft} more min${minsLeft !== 1 ? 's' : ''})`}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => { e.stopPropagation(); window.open(aabUrl, '_blank', 'noopener,noreferrer'); }}
              >
                <CloudDownload fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }

        // Outside the 2-hour window — disable the button. URL may still be
        // valid server-side, but we surface "expired" to match the original
        // UX intent and to keep the table clean of weeks-old artifacts.
        return (
          <Tooltip title="Download window expired (2 hour limit)">
            <span>
              <IconButton size="small" disabled>
                <CloudDownload fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'end_time',
      headerName: 'Last Update',
      width: 116,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value || params.value === 'N/A') return 'N/A';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
            <Typography variant="body2">{formatDateOnly(params.value as string)}</Typography>
            <Typography variant="caption" color="textSecondary">{formatTimeOnly(params.value as string)}</Typography>
          </Box>
        );
      }
    },
  ];

  return (
    <Box
      sx={{
        minHeight: isMobile ? '100%' : 'calc(100vh - 120px)',
        width: '100%',
        p: isMobile ? 0 : 2,
        display: 'flex',
        flexDirection: 'column'
      }}
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <GlobalStyles styles={{ '.row-selected-animate': { backgroundColor: 'rgba(25, 118, 210, 0.08) !important' } }} />
      <div ref={topRef} />

      {/* Backdrops */}
      <Backdrop open={isRefreshing && rows.length === 0} sx={{ color: '#fff', zIndex: 1201, flexDirection: 'column' }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2, fontWeight: 600 }}>Loading App Data...</Typography>
      </Backdrop>

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert severity={snackSeverity} variant="filled">{snackMsg}</Alert>
      </Snackbar>

      <Backdrop open={triggering} sx={{ color: '#fff', zIndex: 1200 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Deployment Table Section */}
      <Paper elevation={0} sx={{
        p: isMobile ? 1 : 4,
        borderRadius: 4,
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 880,
        mb: 2
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3748' }}>
            Deployment Control Center
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                border: '1px solid',
                borderColor: '#e2e8f0',
                borderRadius: 1,
                overflow: 'hidden',
                transition: 'border-color 120ms ease',
                '&:focus-within': { borderColor: 'primary.main' },
              }}
            >
              <Select
                size="small"
                value={searchMode}
                onChange={(e) => handleSearchModeChange(e.target.value as SearchMode)}
                aria-label="Search field"
                sx={{
                  width: 132,
                  bgcolor: '#f8fafc',
                  borderRadius: 0,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': { py: 1 },
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                <MenuItem value="q">Name / Host</MenuItem>
                <MenuItem value="version">Version</MenuItem>
                <MenuItem value="sitekey">Site Key</MenuItem>
              </Select>
              <Box sx={{ width: '1px', bgcolor: '#e2e8f0' }} />
              <TextField
                size="small"
                placeholder={searchMode === 'version' ? 'e.g. 1.2.3' : searchMode === 'sitekey' ? 'e.g. greenwood-isd' : 'Search by app name or host'}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                sx={{
                  width: 280,
                  '& .MuiOutlinedInput-root': { borderRadius: 0 },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <Clear
                        fontSize="small"
                        sx={{ color: 'text.secondary', cursor: 'pointer' }}
                        onClick={handleClearSearch}
                      />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={platformFilter || 'all'}
              onChange={(_, val) => {
                if (val === null) return; // Ignore deselect — keep at least one toggle active
                handlePlatformChange(val === 'all' ? '' : val);
              }}
              sx={{
                height: 40,
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 1.5,
                  border: '1px solid #e2e8f0',
                  color: 'text.secondary',
                  fontWeight: 600,
                  position: 'relative',
                  // Lift the selected button so its colored border draws on
                  // top of its neighbours' borders — fixes the "open on one
                  // side" effect when iOS or Android is active.
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    zIndex: 1,
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.14)',
                  },
                },
              }}
            >
              <ToggleButton value="all" aria-label="All platforms">All</ToggleButton>
              <Tooltip title="iOS only" arrow>
                <ToggleButton value="ios" aria-label="iOS only">
                  <Apple sx={{ fontSize: 18 }} />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Android only" arrow>
                <ToggleButton value="android" aria-label="Android only">
                  <Android sx={{ fontSize: 18 }} />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>

            <Box data-tour="deploy-version-filter" sx={{ position: 'relative', display: 'inline-flex' }}>
              <Tooltip title="Show only apps on a specific version" arrow>
                <Select
                  size="small"
                  displayEmpty
                  value={versionFilter}
                  onChange={(e) => handleVersionFilterSelect(e.target.value)}
                  renderValue={(v) => (v ? `v${v}` : 'All versions')}
                  MenuProps={{
                    // Anchor the list to the field's top edge instead of letting
                    // MUI auto-scroll to centre the selected item — that scroll
                    // was hiding the "All versions" (clear) row off the top.
                    autoFocus: false,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    PaperProps: { sx: { maxHeight: 360 } },
                  }}
                  sx={{
                    height: 40,
                    minWidth: 150,
                    // Leave room for the clear (×) button so the value never sits under it.
                    '& .MuiSelect-select': { pr: versionFilter ? '40px !important' : undefined },
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    bgcolor: versionFilter ? 'rgba(25, 118, 210, 0.08)' : undefined,
                    color: versionFilter ? 'primary.main' : 'text.secondary',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: versionFilter ? 'primary.main' : '#e2e8f0' },
                  }}
                >
                  <MenuItem value="" sx={{ fontStyle: 'italic', color: 'text.secondary', borderBottom: '1px solid #e2e8f0' }}>
                    All versions
                  </MenuItem>
                  {versionOptions.map(v => (
                    <MenuItem key={v} value={v} sx={{ fontSize: '0.85rem' }}>v{v}</MenuItem>
                  ))}
                </Select>
              </Tooltip>
              {versionFilter && (
                <Tooltip title="Clear version filter" arrow>
                  <IconButton
                    size="small"
                    aria-label="Clear version filter"
                    onClick={() => handleVersionFilterSelect('')}
                    sx={{
                      position: 'absolute',
                      right: 28,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'primary.main',
                      p: 0.25,
                    }}
                  >
                    <Clear sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Tooltip title="Sort apps across all pages" arrow>
              <Button
                size="small"
                variant={sortBy ? 'contained' : 'outlined'}
                color={sortBy ? 'primary' : 'inherit'}
                startIcon={<SortRounded sx={{ fontSize: 18 }} />}
                endIcon={<ArrowDropDown />}
                onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                sx={{
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: sortBy ? undefined : '#e2e8f0',
                  color: sortBy ? '#fff' : 'text.secondary',
                  minWidth: 110,
                  justifyContent: 'space-between',
                }}
              >
                {currentSortLabel}
              </Button>
            </Tooltip>
            <Menu
              anchorEl={sortMenuAnchor}
              open={Boolean(sortMenuAnchor)}
              onClose={() => setSortMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 0.5, minWidth: 240, borderRadius: 2 } }}
            >
              <MenuItem
                onClick={() => handleSortSelect('', 'asc')}
                selected={!sortBy}
                sx={{ fontSize: '0.85rem' }}
              >
                <ListItemIcon><Clear fontSize="small" /></ListItemIcon>
                <ListItemText primary="Default (none)" />
              </MenuItem>
              <Divider />
              {SORT_OPTIONS.map(opt => {
                const selected = sortBy === opt.field && sortOrder === opt.order;
                return (
                  <MenuItem
                    key={`${opt.field}-${opt.order}`}
                    onClick={() => handleSortSelect(opt.field, opt.order)}
                    selected={selected}
                    sx={{ fontSize: '0.85rem' }}
                  >
                    <ListItemIcon>
                      {opt.order === 'asc'
                        ? <ArrowUpward fontSize="small" />
                        : <ArrowDownward fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                );
              })}
            </Menu>

            {(searchInput || platformFilter || sortBy) && (
              <Button
                size="small"
                onClick={handleClearAllFilters}
                aria-label="Clear all filters"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main', backgroundColor: 'rgba(244, 67, 54, 0.08)' },
                }}
              >
                Clear filters
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 2 }} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1.5 }}>
            <Tooltip title="Trigger Patch Update">
              <Button
                variant="outlined"
                onClick={() => handleTriggerClick(true)}
                disabled={triggering || selectedRows.length !== 1}
                className={classes.refreshButton}
                sx={{ px: 3 }}
              >
                PATCH UPDATE
              </Button>
            </Tooltip>

            <Tooltip title={`Deploy version ${selectedTag}`}>
              <Button
                data-tour="deploy-button"
                variant="outlined"
                onClick={() => handleTriggerClick(false)}
                disabled={triggering || selectedRows.length === 0}
                className={classes.refreshButton}
                sx={{ minWidth: 220, px: 3 }}
              >
                DEPLOY {selectedTag ? `VERSION ${selectedTag}` : '...'}
              </Button>
            </Tooltip>

            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                onClick={() => loadTableData(true)}
                disabled={isRefreshing}
                className={classes.refreshButton}
                sx={{ px: 3 }}
              >
                REFRESH
              </Button>
              <LinearProgress variant="determinate" value={progress} sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3 }} />
            </Box>

            <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

            <ButtonGroup variant="outlined" sx={{ height: 40, '& .MuiButton-root': { height: 40, textTransform: 'none', fontWeight: 600, px: 2 } }}>
              <Tooltip title="Select every app matching the current filters across all pages" arrow>
                <Button onClick={selectAllApps} startIcon={<PlaylistAddCheck />} disabled={isSelectingAll}>
                  {isSelectingAll ? 'Loading…' : 'Select all apps'}
                </Button>
              </Tooltip>
              <Tooltip title="Clear the current selection (all selected apps, across pages)" arrow>
                <span>
                  <Button onClick={unselectAll} startIcon={<DeleteSweep />} disabled={selectedRows.length === 0}>
                    Unselect
                  </Button>
                </span>
              </Tooltip>
            </ButtonGroup>

            <Tooltip title={notificationsEnabled ? 'Browser notifications on — click to disable' : 'Get a browser notification when your builds finish'} arrow>
              <IconButton
                data-tour="notifications-bell"
                onClick={toggleNotifications}
                sx={{
                  height: 40,
                  width: 40,
                  border: '1px solid',
                  borderColor: notificationsEnabled ? 'primary.main' : '#e2e8f0',
                  borderRadius: 1,
                  color: notificationsEnabled ? 'primary.main' : 'text.secondary',
                  bgcolor: notificationsEnabled ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                }}
              >
                {notificationsEnabled ? <NotificationsActive fontSize="small" /> : <NotificationsNone fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction="column" alignItems="flex-end" spacing={0.75}>
            <Typography variant="caption" color="text.secondary">
              {isRefreshing ? 'Refreshing...' : `Last updated ${cacheAge}s ago`}
            </Typography>
            {selectedRows.length > 0 && (() => {
              return (
                <Tooltip title="View selected apps" placement="left" arrow>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectionDrawerOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectionDrawerOpen(true);
                      }
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      cursor: 'pointer',
                      color: '#fff',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                      transition: 'transform 120ms ease, box-shadow 120ms ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 18px rgba(99, 102, 241, 0.45)',
                      },
                      '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.22)',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        lineHeight: 1,
                      }}
                    >
                      {selectedRows.length}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'inherit' }}>
                        app{selectedRows.length !== 1 ? 's' : ''} selected
                      </Typography>
                    </Box>
                    <ChevronRight sx={{ fontSize: 20, opacity: 0.9 }} />
                  </Box>
                </Tooltip>
              );
            })()}
          </Stack>
        </Stack>

        <Paper data-tour="deploy-table" sx={{ ...dataGridContainerSx, flex: 1, width: '100%', minHeight: 400 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection
            // Clicking the row body opens the app-details drawer; only the
            // checkbox toggles selection for deployment.
            disableRowSelectionOnClick
            onRowClick={(params, event) => {
              if ((event.target as HTMLElement)?.closest?.('.MuiDataGrid-cellCheckbox')) return;
              setDetailsApp(toAppDetails(params.row));
            }}
            keepNonExistentRowsSelected
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={(newSelection) => {
              const newIds = newSelection as number[];
              const currentPageIds = new Set(rows.map(r => r.id));

              // Preserve selections from OTHER pages, merge with current page changes
              const otherPageSelections = selectedRows.filter(id => !currentPageIds.has(id));
              const currentPageSelections = newIds.filter(id => currentPageIds.has(id));
              const merged = [...otherPageSelections, ...currentPageSelections];

              // Detect deselections on the current page only
              const prevCurrentPage = selectedRows.filter(id => currentPageIds.has(id));
              const deselected = prevCurrentPage.filter(id => !currentPageSelections.includes(id));
              if (deselected.length > 0) {
                setSelectedAabRows(prev => { const s = new Set(prev); deselected.forEach(id => s.delete(id)); return s; });
                setSelectedScreenshotRows(prev => { const s = new Set(prev); deselected.forEach(id => s.delete(id)); return s; });
              }

              setSelectedRows(merged);
            }}
            loading={isRefreshing}
            getRowClassName={(params) => selectedRows.includes(params.id as number) ? 'row-selected-animate' : ''}
            autoHeight={false}
            rowHeight={56}
            sx={{
              ...dataGridSx,
              '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': { display: 'none' },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
            }}
          />
        </Paper>

        <AppDetailsDrawer app={detailsApp} onClose={() => setDetailsApp(null)} />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ position: 'fixed', bottom: 80, right: 20, zIndex: 1300 }}
        >
          <Fab color="primary" onClick={handleScrollDown} aria-label="Scroll down to bottom">
            <ArrowDownward />
          </Fab>
        </motion.div>

        {showScrollUp && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} whileHover={{ scale: 1.15, boxShadow: '0 0 18px rgba(156,39,176,0.75)' }} whileTap={{ scale: 0.95 }} style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1300 }}>
            <Fab color="secondary" onClick={smoothScrollToTop} aria-label="Back to top"><ArrowUpward /></Fab>
          </motion.div>
        )}

        <div ref={bottomRef} />

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}
      </Paper>

      <Drawer
        anchor="right"
        open={selectionDrawerOpen && selectedRows.length > 0}
        onClose={() => setSelectionDrawerOpen(false)}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(15, 23, 42, 0.35)',
            },
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.92) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '-12px 0 32px -8px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: '#fff',
                }}
              >
                <Badge
                  badgeContent={selectedRows.length}
                  color={selectedRows.length > 18 ? 'error' : 'default'}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.65rem',
                      height: 16,
                      minWidth: 16,
                      backgroundColor: selectedRows.length > 18 ? undefined : '#fff',
                      color: selectedRows.length > 18 ? '#fff' : '#6366f1',
                      fontWeight: 700,
                    },
                  }}
                >
                  <Inventory2Outlined sx={{ fontSize: 20 }} />
                </Badge>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c', lineHeight: 1.2 }}>
                  Selected Apps
                </Typography>
                <Typography variant="caption" sx={{ color: selectedRows.length > 18 ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
                  {selectedRows.length} of 18 max{selectedRows.length > 18 ? ' — exceeds limit!' : ''}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => setSelectionDrawerOpen(false)} aria-label="Close selection panel" size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: 'rgba(226, 232, 240, 0.7)' }} />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
            <Stack spacing={1.25}>
              {selectedRows.map((id) => {
                const row = rowCacheRef.current.get(id);
                const platform = (row?.platform || '').toLowerCase();
                const isIOS = platform === 'ios';
                const aabFlag = selectedAabRows.has(id);
                const screenshotFlag = selectedScreenshotRows.has(id);
                const hasFlag = aabFlag || screenshotFlag;
                return (
                  <Paper
                    key={id}
                    elevation={0}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      backgroundColor: hasFlag ? 'rgba(99, 102, 241, 0.06)' : 'rgba(255, 255, 255, 0.75)',
                      border: '1px solid',
                      borderColor: hasFlag ? 'rgba(99, 102, 241, 0.4)' : 'rgba(226, 232, 240, 0.9)',
                      borderLeftWidth: hasFlag ? 3 : 1,
                      borderLeftColor: hasFlag ? '#6366f1' : 'rgba(226, 232, 240, 0.9)',
                      transition: 'all 150ms ease',
                      '&:hover': {
                        borderColor: 'rgba(99, 102, 241, 0.5)',
                        backgroundColor: hasFlag ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.95)',
                        transform: 'translateX(-2px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar
                        src={row?.app_icon_url}
                        variant="rounded"
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: isIOS ? '#0f172a' : '#3DDC84',
                        }}
                      >
                        {isIOS ? <Apple sx={{ fontSize: 18 }} /> : <Android sx={{ fontSize: 18 }} />}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#1a202c',
                            lineHeight: 1.25,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row?.appName || row?.composerHost || `App #${id}`}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row?.composerHost || '—'}{row?.version ? ` • v${row.version}` : ''}
                        </Typography>
                      </Box>
                      {platform && (
                        <Chip
                          label={isIOS ? 'iOS' : 'Android'}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: isIOS ? 'rgba(15, 23, 42, 0.08)' : 'rgba(61, 220, 132, 0.15)',
                            color: isIOS ? '#0f172a' : '#1b8a47',
                          }}
                        />
                      )}
                      <Tooltip title="Deselect" arrow>
                        <IconButton
                          size="small"
                          onClick={() => deselectRow(id)}
                          aria-label={`Deselect ${row?.appName || `app ${id}`}`}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main', backgroundColor: 'rgba(244, 67, 54, 0.08)' },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {hasFlag && (
                      <Stack direction="row" spacing={0.75} sx={{ pl: 5.5 }}>
                        {aabFlag && (
                          <Chip
                            icon={<ArchiveOutlined sx={{ fontSize: 13 }} />}
                            label="AAB"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: 'rgba(34, 197, 94, 0.12)',
                              color: '#15803d',
                              border: '1px solid rgba(34, 197, 94, 0.35)',
                              '& .MuiChip-icon': { color: '#15803d', ml: 0.5 },
                            }}
                          />
                        )}
                        {screenshotFlag && (
                          <Chip
                            icon={<PhotoCameraOutlined sx={{ fontSize: 13 }} />}
                            label="Screenshot"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: 'rgba(245, 158, 11, 0.12)',
                              color: '#b45309',
                              border: '1px solid rgba(245, 158, 11, 0.35)',
                              '& .MuiChip-icon': { color: '#b45309', ml: 0.5 },
                            }}
                          />
                        )}
                      </Stack>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: 'rgba(226, 232, 240, 0.7)' }} />

          <Box sx={{ px: 3, py: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<DeleteSweep />}
              onClick={clearAllSelections}
              sx={{ fontWeight: 600, textTransform: 'none' }}
            >
              Clear all selections
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Pre-deploy confirmation modal — summarises the batch, surfaces any
          in-flight conflicts, and asks for an explicit Confirm before
          actually hitting the trigger endpoint. */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, isPatch: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {confirmDialog.open && (() => {
          const targets = selectedRows
            .map(id => rowCacheRef.current.get(id))
            .filter((r): r is Row => !!r);
          const iosCount = targets.filter(r => (r.platform || '').toLowerCase() === 'ios').length;
          const androidCount = targets.filter(r => (r.platform || '').toLowerCase() === 'android').length;
          const aabCount = targets.filter(r => selectedAabRows.has(r.id)).length;
          const screenshotCount = targets.filter(r => selectedScreenshotRows.has(r.id)).length;
          const inFlight = inFlightSelected();
          return (
            <>
              <DialogTitle sx={{ pr: 6 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
                    <Inventory2Outlined sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      Confirm {confirmDialog.isPatch ? 'Patch Update' : 'Deploy'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {targets.length} app{targets.length !== 1 ? 's' : ''} · {confirmDialog.isPatch ? 'patch version bump' : `version ${selectedTag || '—'}`}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton onClick={() => setConfirmDialog({ open: false, isPatch: false })} sx={{ position: 'absolute', right: 8, top: 8 }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                    <Chip size="small" icon={<Apple sx={{ fontSize: 14 }} />} label={`iOS: ${iosCount}`} sx={{ fontWeight: 700, bgcolor: 'rgba(15, 23, 42, 0.08)', color: '#0f172a' }} />
                    <Chip size="small" icon={<Android sx={{ fontSize: 14 }} />} label={`Android: ${androidCount}`} sx={{ fontWeight: 700, bgcolor: 'rgba(61, 220, 132, 0.15)', color: '#1b8a47' }} />
                    {aabCount > 0 && (
                      <Chip size="small" icon={<ArchiveOutlined sx={{ fontSize: 14 }} />} label={`AAB: ${aabCount}`} sx={{ fontWeight: 700, bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#15803d' }} />
                    )}
                    {screenshotCount > 0 && (
                      <Chip size="small" icon={<PhotoCameraOutlined sx={{ fontSize: 14 }} />} label={`Screenshot: ${screenshotCount}`} sx={{ fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#b45309' }} />
                    )}
                  </Stack>

                  {inFlight.length > 0 && (
                    <Alert severity="warning" icon={<WarningAmberRounded />}>
                      <AlertTitle sx={{ fontWeight: 700 }}>{inFlight.length} app{inFlight.length !== 1 ? 's' : ''} already in flight</AlertTitle>
                      <Typography variant="caption" component="div">
                        Triggering will queue another build on top of the existing one. Consider waiting for the current run to finish.
                      </Typography>
                      <List dense sx={{ mt: 0.5, py: 0 }}>
                        {inFlight.slice(0, 6).map(r => (
                          <ListItem key={r.id} sx={{ py: 0, px: 0 }}>
                            <Typography variant="caption">• {r.appName} ({r.platform}) — {r.status}</Typography>
                          </ListItem>
                        ))}
                        {inFlight.length > 6 && (
                          <ListItem sx={{ py: 0, px: 0 }}>
                            <Typography variant="caption" color="text.secondary">…and {inFlight.length - 6} more</Typography>
                          </ListItem>
                        )}
                      </List>
                    </Alert>
                  )}

                  <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1 }}>
                    {targets.map(r => (
                      <Stack key={r.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                        {(r.platform || '').toLowerCase() === 'ios' ? <Apple sx={{ fontSize: 14, color: '#0f172a' }} /> : <Android sx={{ fontSize: 14, color: '#3DDC84' }} />}
                        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>{r.appName}</Typography>
                        <Typography variant="caption" color="text.secondary">v{r.version}</Typography>
                      </Stack>
                    ))}
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    {notificationsEnabled
                      ? 'Browser notifications are enabled — you\'ll be pinged when each build finishes.'
                      : 'Tip: enable the bell icon above to get notified when each build finishes.'}
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setConfirmDialog({ open: false, isPatch: false })} color="inherit">Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => executeTrigger(confirmDialog.isPatch)}
                  sx={{ fontWeight: 700, textTransform: 'none', minWidth: 160, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
                >
                  {confirmDialog.isPatch ? 'Confirm patch' : `Deploy ${targets.length} app${targets.length !== 1 ? 's' : ''}`}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Notification help dialog — opens once after the user enables the bell
          for the first time, alongside an actual sample OS notification. */}
      <Dialog
        open={notifHelpOpen}
        onClose={() => setNotifHelpOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
              <NotificationsActive sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                Build notifications enabled
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Here's how it works — click <strong>Got it</strong> below to receive a confirmation ping.
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={() => setNotifHelpOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {/* In-page preview of the notification card */}
            <Box>
              <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em' }}>
                What the notification looks like
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  mt: 0.75,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: '#16a34a',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                    ✅ Example build notification
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.25, lineHeight: 1.4 }}>
                    schoolbuzz.sites.finalsite.dev (android) v200.35.7 — success
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                    Real notifications appear in your OS notification center — top-right on macOS, action center on Windows. Click <strong>Got it</strong> below and you will receive a confirmation ping.
                  </Typography>
                </Box>
              </Paper>
            </Box>

            {/* Behavior matrix */}
            <Box>
              <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em' }}>
                When notifications fire
              </Typography>
              <List dense disablePadding sx={{ mt: 0.5 }}>
                <BehaviorRow ok title="App Deployment page is open, tab in focus">
                  Real builds you trigger from here ping you within ~12 seconds of reaching success / failed / cancelled.
                </BehaviorRow>
                <BehaviorRow ok title="App Deployment page is open, you switched to a different tab">
                  Still fires. The notification surfaces in your OS notification center (top-right on macOS, bottom-right or action center on Windows).
                </BehaviorRow>
                <BehaviorRow ok title="App Deployment page is open, browser minimized">
                  Still fires at the OS level. Clicking the notification refocuses this tab.
                </BehaviorRow>
                <BehaviorRow warn title="You navigated to a different App Manager page (same tab)">
                  Build-status polling pauses on other pages — pending notifications resume the moment you come back to App Deployment.
                </BehaviorRow>
                <BehaviorRow bad title="You closed this tab or the whole browser">
                  Nothing fires — the dashboard's JavaScript stops running, so the poller can't detect when builds finish.
                </BehaviorRow>
                <BehaviorRow bad title="You blocked notifications in your browser settings">
                  No notifications. Re-enable them in the browser's site settings (lock icon in the URL bar) and click the bell again.
                </BehaviorRow>
              </List>
            </Box>

            {/* OS-level permission gate. Even after browser permission is
                granted, both macOS and Windows can silently drop notifications
                if the browser is blocked at the OS level — and there's no
                browser-side signal that this has happened. Surfacing the exact
                paths here means an operator who hasn't seen a banner can
                self-diagnose without pinging us. */}
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>Not seeing notifications? Check your OS settings.</AlertTitle>
              <Typography variant="caption" component="div" sx={{ lineHeight: 1.6 }}>
                Browser permission alone isn't enough — your operating system has a second permission layer that can silently drop notifications even after you accepted the browser prompt.
              </Typography>

              <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Box sx={{ p: 1.25, border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: 1.5, bgcolor: 'rgba(255, 255, 255, 0.6)' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#0f172a', display: 'block', mb: 0.5 }}>
                    🍎 macOS
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ lineHeight: 1.55, color: '#334155' }}>
                    Open <strong>System Settings → Notifications</strong>, scroll to your browser (Chrome / Edge / Safari) and verify:
                    <br />• <strong>Allow notifications</strong> is on
                    <br />• <strong>Notification style</strong> is set to <em>Alerts</em> or <em>Banners</em> (<em>None</em> drops them)
                    <br />• <strong>Show in Notification Center</strong> is on
                    <br />• <strong>Focus / Do Not Disturb</strong> is off, or your browser is on its allowed-during-Focus list
                  </Typography>
                </Box>

                <Box sx={{ p: 1.25, border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: 1.5, bgcolor: 'rgba(255, 255, 255, 0.6)' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#0f172a', display: 'block', mb: 0.5 }}>
                    🪟 Windows
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ lineHeight: 1.55, color: '#334155' }}>
                    Open <strong>Settings → System → Notifications</strong> and verify:
                    <br />• The top-level <strong>Notifications</strong> toggle is on
                    <br />• Find your browser (Chrome / Edge) in the per-app list and make sure its toggle is on
                    <br />• <strong>Show notification banners</strong> is enabled for your browser
                    <br />• <strong>Focus assist / Do not disturb</strong> is off (Settings → System → Focus)
                  </Typography>
                </Box>
              </Box>

              <Typography variant="caption" component="div" sx={{ mt: 1.25, fontStyle: 'italic', color: '#78350f' }}>
                Tip: after changing OS settings, click the bell off and on again so the dashboard fires a fresh confirmation ping you can use to verify.
              </Typography>
            </Alert>

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>A few important notes</AlertTitle>
              <Typography variant="caption" component="div">
                • Only builds <strong>you trigger from this page</strong> are watched. Other operators' deploys won't notify you.<br />
                • Watched pipelines persist in your browser between page reloads — if a build finishes after you reloaded, you'll still get pinged.<br />
                • One notification per build; we won't spam you with repeats.<br />
                • Click the bell again to disable.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              // The in-app snackbar is the PRIMARY confirmation — it always
              // shows, no matter what the OS does. The browser Notification
              // call is best-effort: on macOS Chrome a focused tab routes
              // notifications straight to Notification Center silently, and
              // even when a banner would normally appear, the OS-level
              // "Allow Chrome to send notifications" setting in System
              // Settings → Notifications can drop the notification on the
              // floor without telling the page.
              const permission = ('Notification' in window) ? Notification.permission : 'unsupported';
              let osCallSucceeded = false;
              let osCallError: unknown = null;
              if (permission === 'granted') {
                try {
                  // Unique tag per fire — a stable tag makes the OS
                  // de-duplicate repeats, so toggling the bell on a second
                  // time would silently suppress the alert.
                  new Notification('✅ Alerts are now active', {
                    body: 'You will be notified here when builds you trigger from this page reach success / failed / cancelled.',
                    tag: `fsam-alerts-active-${Date.now()}`,
                  });
                  osCallSucceeded = true;
                } catch (err) {
                  osCallError = err;
                }
              }
              // Diagnostic line — visible in DevTools when the OS Notification
              // Center doesn't show anything, so it's clear whether the call
              // ran and what permission state the browser thinks it has.
              // eslint-disable-next-line no-console
              console.info('[fsam:notifications]', {
                permission,
                osCallSucceeded,
                osCallError,
                tabFocused: document.hasFocus(),
                hint: permission !== 'granted'
                  ? 'Browser permission not granted. Toggle the bell again and accept the prompt, or check Chrome → Settings → Privacy → Site Settings → Notifications.'
                  : osCallSucceeded && !document.hasFocus()
                    ? 'OS notification should appear momentarily.'
                    : osCallSucceeded
                      ? 'OS dropped the banner because this tab is focused. Check Notification Center, or check macOS → System Settings → Notifications → Chrome to confirm Chrome can show alerts.'
                      : 'Notification constructor threw — see osCallError.',
              });

              // Always tell the user something visible.
              if (permission !== 'granted') {
                openSnack(
                  'Alerts are enabled in App Manager, but the browser has not granted notification permission. Click the bell again and accept the prompt.',
                  'warning',
                );
              } else if (!osCallSucceeded) {
                openSnack(
                  '✅ Alerts are now active. (Confirmation notification could not be sent — see console for details.)',
                  'warning',
                );
              } else {
                openSnack(
                  '✅ Alerts are now active. If you do not see a notification in your OS notification center, check System Settings → Notifications → Chrome.',
                  'success',
                );
              }
              setNotifHelpOpen(false);
            }}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

/** Behavior matrix row used inside the notifications help dialog. */
const BehaviorRow: React.FC<{ ok?: boolean; warn?: boolean; bad?: boolean; title: string; children: React.ReactNode }> = ({ ok, warn, bad, title, children }) => {
  const color = ok ? '#16a34a' : warn ? '#f59e0b' : bad ? '#dc2626' : '#94a3b8';
  const bg = ok ? 'rgba(22, 163, 74, 0.1)' : warn ? 'rgba(245, 158, 11, 0.12)' : bad ? 'rgba(220, 38, 38, 0.1)' : 'rgba(148, 163, 184, 0.1)';
  const symbol = ok ? '✓' : warn ? '!' : bad ? '✗' : '·';
  return (
    <ListItem disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          bgcolor: bg,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 800,
          mr: 1.25,
          mt: 0.15,
          flexShrink: 0,
        }}
      >
        {symbol}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25, lineHeight: 1.5 }}>
          {children}
        </Typography>
      </Box>
    </ListItem>
  );
};